import React, { useEffect, useState } from 'react';
import { FiCheckCircle } from 'react-icons/fi';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import EllipsisLoader from '../../../base_component/EllipsisLoader/EllipsisLoader';
import Dialog from '../../../base_component/Dialog/Dialog';
import axios from 'axios';
import { FREE_SUBSCRIBE } from '../../../service/Api';
import toast from 'react-hot-toast';
import { X } from 'lucide-react';
import { BsInfoCircleFill } from "react-icons/bs";
import { checkoutSubscriptionAPI, getSubscriptionPlanAPI } from '../../../service/ApiServices';
import { SubscriptionPlanSkeleton } from '../../../components/Skeleton';

export default function Subscription() {
    const navigate = useNavigate()
    const [selectedPlan, setSelectedPlan] = useState('');
    const [changebtn, setChangebtn] = useState({});
    const [planDetails, setPlanDetails] = useState({});
    const [isPaymentLoading, setIsPaymentLoading] = useState(false);
    const [showPlanInfoModal, setShowPlanInfoModal] = useState(false);
    const [agreeToTerms, setAgreeToTerms] = useState(false);
    const [subscriptionPlan, setSubscriptionPlan] = useState([]);
    const [isFreeTrialExpired, setIsFreeTrialExpired] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [showTermsError, setShowTermsError] = useState(false);
    const location = useLocation();
    const token = location?.state?.token;
    const userId = localStorage.getItem("hoast_user_id");

    const getSubscriptionPlan = () => {
        setIsLoading(true);
        getSubscriptionPlanAPI(token)
            .then((res) => {
                const plans = res?.data?.data?.available_plans;
                const isTrialExpired = res?.data?.data?.user_subscription_details?.is_free_trial_expired;

                setSubscriptionPlan(plans);
                setIsFreeTrialExpired(isTrialExpired);

                if (isTrialExpired === true) {
                    const paidPlan = plans?.find(plan => plan.plan_type !== "free_tier");
                    if (paidPlan) {
                        setSelectedPlan(paidPlan.id);
                        setChangebtn(paidPlan);
                    }
                } else {
                    const freePlan = plans?.find(plan => plan.plan_type === "free_tier");
                    if (freePlan) {
                        setSelectedPlan(freePlan.id);
                        setChangebtn(freePlan);
                    }
                }
            })
            .catch((err) => {
                const message = err?.response?.data?.message;
                toast.error(message || "Something went wrong!");
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    useEffect(() => {
        getSubscriptionPlan();
    }, []);

    const handleSubmit = async () => {
        if (!agreeToTerms) {
            setShowTermsError(true);
            toast.error("Please agree to Terms & Conditions and Privacy Policy to continue");
            return;
        }
        setShowTermsError(false);
        setIsPaymentLoading(true);
        if (changebtn?.plan_type !== "free_tier") {
            checkoutSubscriptionAPI({
                stripe_product_id: changebtn?.stripe_product_id,
                user_id: userId,
            })
                .then((res) => {
                    if (res?.data?.status === 1) {
                        const checkoutUrl = res?.data?.data?.url;
                        window.location.href = checkoutUrl;
                    } else {
                        toast.error(res?.data?.message);
                    }
                })
                .catch((err) => {
                    const message = err?.response?.data?.message;
                    toast.error(message || "Something went wrong!");
                })
                .finally(() => {
                    setIsPaymentLoading(false);
                });
            return;
        }
        try {
            await axios.post(FREE_SUBSCRIBE, {}, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            localStorage.setItem("plan", "yes");
            localStorage.setItem("selectedPlan", selectedPlan);
            window.dispatchEvent(new Event('planStatusChanged'));
            navigate("/account_setup");
        } catch (error) {
            toast.error(error?.response?.data?.message?.message);
        } finally {
            setIsPaymentLoading(false);
        }
    };

    return (
        <div className="h-screen flex flex-col items-center justify-center px-4">
            <div className="bg-white md:p-8 p-5 rounded-xl sm:h-fit h-[80vh] overflow-y-auto shadow-md w-full max-w-2xl">
                <div className="md:px-20 px-0">
                    <h1 className="md:text-2xl text-xl font-semibold text-center">Welcome!</h1>
                    <p className="text-gray-500 md:text-base text-sm text-center mt-2">
                        Discover and showcase your work to a community of art lovers.
                    </p>

                    <h2 className="text-center mt-8 md:text-base text-sm font-medium">
                        How would you like to start your artistic journey?
                    </h2>

                    {isLoading ? (
                        <SubscriptionPlanSkeleton />
                    ) : (
                        <div className="mt-6 space-y-4 h-[40vh] overflow-y-auto custom-scrollbar">
                            {subscriptionPlan?.map((plan) => (
                                <label
                                    key={plan.id}
                                    htmlFor={plan.id}
                                    onClick={(e) => {
                                        if (isFreeTrialExpired === true && plan.plan_type === "free_tier") {
                                            e.preventDefault();
                                            return;
                                        }
                                    }}
                                    className={`relative flex items-center p-4 py-6 rounded-xl border-2 cursor-pointer transition-all ${isFreeTrialExpired === true && plan.plan_type === "free_tier" ? "opacity-50 cursor-not-allowed" : ""} ${selectedPlan === plan.id
                                        ? 'border-[#24b565a9] bg-white shadow-md'
                                        : 'border-gray-200 bg-white'
                                        }`}
                                >
                                    <input
                                        type="radio"
                                        id={plan.id}
                                        name="subscription"
                                        value={plan.id}
                                        checked={selectedPlan === plan.id}
                                        onChange={() => {
                                            setSelectedPlan(plan.id);
                                            setChangebtn(plan);
                                        }}
                                        className="sr-only"
                                    />
                                    <div className="flex-1">
                                        <p className="md:text-lg text-base font-semibold"> {plan.plan_type === "free_tier" ? 'Free' : '$' + plan.amount}
                                            <span className={`${plan.plan_type !== "free_tier" ? "ml-0 text-sm font-normal" : "ml-2 md:text-lg text-base font-semibold"}`}>
                                                {plan.plan_type === "free_tier" ? plan.billing_interval + " " + plan.billing_period : '/' + ' ' + plan.billing_interval + " " + plan.billing_period}
                                            </span>
                                        </p>
                                        <p className="md:text-sm text-xs text-gray-500 mt-1">{plan.plan_name}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setShowPlanInfoModal(true);
                                                setPlanDetails(plan);
                                            }}
                                            className="text-gray-400 hover:text-gray-600 absolute top-2 right-2 transition-colors"
                                        >
                                            <BsInfoCircleFill className="text-lg" />
                                        </button>
                                        {selectedPlan === plan.id && (
                                            <FiCheckCircle className={`text-[#24B565] text-xl`} />
                                        )}
                                    </div>
                                </label>
                            ))}
                        </div>
                    )}

                    <div className="flex items-start space-x-2 py-3">
                        <input
                            type="checkbox"
                            id="agreeToTerms"
                            checked={agreeToTerms}
                            onChange={(e) => {
                                setAgreeToTerms(e.target.checked);
                                if (e.target.checked) {
                                    setShowTermsError(false);
                                }
                            }}
                            className={`mt-0.5 h-4 w-4 custom-checkbox ${showTermsError ? "checkbox-error border-red-500" : ""}`}
                        />
                        <div htmlFor="agreeToTerms" className={`text-sm select-none`}>
                            I read and agree to{" "}
                            <Link to="/terms-conditions" className="font-semibold text-[#1E1E1E] hover:text-[#24B565] transition-colors">
                                Terms & Conditions
                            </Link>{" "}
                            and{" "}
                            <Link to="/privacy-policy" className="font-semibold text-[#1E1E1E] hover:text-[#24B565] transition-colors">
                                Privacy Policy
                            </Link>
                        </div>
                    </div>

                    <button
                        onClick={handleSubmit}
                        disabled={isPaymentLoading || isLoading}
                        className="w-full bg-[#212121] text-sm text-white rounded-xl py-4 mt-8 hover:bg-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                        {isPaymentLoading ? (
                            <EllipsisLoader color="white" />
                        ) : (
                            selectedPlan === changebtn?.id && changebtn?.plan_type !== "free_tier" ? "Make Payment" : "Start Free Trial"
                        )}
                    </button>

                    <div className='text-center mt-5'>
                        <Link to={"/account_setup"} className="text-sm text-[#667085] cursor-pointer">
                            Maybe later
                        </Link>
                    </div>
                </div>
            </div>

            <Dialog
                open={showPlanInfoModal}
                onClose={() => setShowPlanInfoModal(false)}
                size="md"
            >
                <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                <Dialog.Panel>
                    <div className='flex justify-between items-center px-8 my-5'>
                        <h1 className="text-xl font-semibold text-black">{planDetails?.plan_name}</h1>

                        <button
                            className="bg-[#F4F5FA] text-sm rounded-xl sm:mx-3 mx-2 max-w-6 h-6 max-h-6 p-0.5"
                            onClick={() => setShowPlanInfoModal(false)}
                        >
                            <X className="h-full w-full" />
                        </button>
                    </div>
                    <Dialog.Description>
                        <div className="flex text-start px-8">
                            <div>
                                <div className='text-start'>
                                    <p className="md:text-xl text-base font-semibold"> {planDetails.plan_type === "free_tier" ? 'Free' : '$' + planDetails.amount}
                                        <span className={`${planDetails.plan_type !== "free_tier" ? "ml-0 text-sm font-normal" : "ml-2 md:text-lg text-base font-semibold"}`}>
                                            {planDetails.plan_type === "free_tier" ? planDetails.billing_interval + " " + planDetails.billing_period : '/' + ' ' + planDetails.billing_interval + " " + planDetails.billing_period}
                                        </span>
                                    </p>
                                </div>
                                <p className="md:text-base text-sm text-black text-start my-3">
                                    {planDetails?.plan_desc}
                                </p>
                            </div>
                        </div>
                    </Dialog.Description>
                </Dialog.Panel>
            </Dialog>
        </div>
    );
}
